import { useCallback, useEffect, useMemo, useRef } from 'react';
import { clearCustomer, clearOrder, setAppliedPromotions, setCurrentOrder, setOrderSalesperson, setItemSalesperson } from '../../store/orderSlice';
import { setProductSearch } from '../../store/productSlice';
import { useLazyGetProductByIdQuery } from '../../services/productApi';

// Highest quantity of an item the client currently believes is sellable.
// Advisory (the cart line's own stock snapshot can lag reality between
// fetch and checkout) — the backend precheck is still the authoritative
// check and syncCartToBackend's catch reconciles the cart to whatever it
// reports.
const stockCap = (item) => {
  const q = item?.stock?.total_quantity;
  return Number.isFinite(q) ? q : Infinity;
};

export default function useDashboardOrderFlow({
  buildDraftCreatePayload,
  billingMode,
  cancelOrder,
  cart,
  cartRef,
  createOrder,
  currentOrderIdRef,
  customerRequired,
  dispatch,
  exchangeSourceOrder,
  grand_total,
  holdOrder,
  orderCartItemsRef,
  orderNotesRef,
  salespersonRequired,
  selectedCustomer,
  selectedProduct,
  selectedSalesperson,
  setCart,
  setExchangeNewItems,
  setIsCheckoutOpen,
  setIsCustomerModalOpen,
  setIsHoldDrawerOpen,
  setIsSalespersonDrawerOpen,
  setOrderNotes,
  setSelectedProduct,
  setSnackbar,
  showToast,
  triggerGetOrder,
  updateOrder,
  removeLoyalty,
}) {
  const [triggerGetProductById] = useLazyGetProductByIdQuery();
  const updateQtyTimer = useRef(null);
  // Chains sync calls so a second add-to-cart click fired before the first
  // request's response (and the redux round-trip that updates currentOrderIdRef)
  // completes can't race it: both would otherwise see currentOrderIdRef.current
  // as null, both would POST /orders, and whichever response resolved last would
  // silently overwrite redux — sometimes with the stale, promotion-less order.
  const syncChainRef = useRef(Promise.resolve());

  const syncCartToBackend = useCallback((cartToSync) => {
    // Plain recursive helper (not the useCallback-wrapped identifier above) so
    // the insufficient_stock retry below can call itself directly.
    const attempt = async (items) => {
      if (items.length === 0) return;

      const backendItems = orderCartItemsRef.current;
      const orderItems = items.map((item) => {
        const backendItem = backendItems.find((backend) => backend.product_id === item.id);
        return {
          ...(backendItem ? { id: backendItem.order_item_id } : {}),
          product_id: item.id,
          quantity: item.qty,
          discount_amount: '0.00',
        };
      });

      const destroyItems = backendItems
        .filter((backend) => !items.find((cartItem) => cartItem.id === backend.product_id))
        .map((backend) => ({ id: backend.order_item_id, _destroy: true }));

      const allItems = [...orderItems, ...destroyItems];

      try {
        if (!currentOrderIdRef.current) {
          const createPayload = buildDraftCreatePayload({
            allItems,
            selectedCustomer,
            selectedSalesperson,
            notes: orderNotesRef.current,
          });
          const res = await createOrder(createPayload).unwrap();
          // Set synchronously — don't rely solely on the redux round-trip (dispatch
          // -> re-render -> effect) to update this ref before the next queued run.
          currentOrderIdRef.current = res.data.order.id;
          dispatch(setCurrentOrder(res.data.order));
          dispatch(setAppliedPromotions({
            applied_promotions: res.data.applied_promotions || [],
            near_miss_hints: res.data.near_miss_hints || [],
          }));
        } else {
          const res = await updateOrder({ id: currentOrderIdRef.current, order_items_attributes: allItems }).unwrap();
          dispatch(setCurrentOrder(res.data.order));
          dispatch(setAppliedPromotions({
            applied_promotions: res.data.applied_promotions || [],
            near_miss_hints: res.data.near_miss_hints || [],
          }));
        }
      } catch (err) {
        // Stock moved out from under us between the optimistic cart update and
        // this sync — snap the offending line back to what's actually available
        // (or drop it) instead of leaving the cart showing a quantity the
        // backend just refused, and re-sync so the correction actually lands.
        const stockDetails = err.data?.details;
        if (stockDetails?.code === 'insufficient_stock' && stockDetails.product_id != null) {
          const staleItem = cartRef.current.find((item) => item.id === stockDetails.product_id);
          const cappedQty = Math.max(0, Math.floor(stockDetails.available ?? 0));
          const correctedCart = cartRef.current
            .map((item) => item.id === stockDetails.product_id ? { ...item, qty: cappedQty } : item)
            .filter((item) => item.qty > 0);
          setCart(correctedCart);
          showToast(
            cappedQty > 0
              ? `Only ${cappedQty} left for ${staleItem?.name || 'item'} — quantity adjusted`
              : `${staleItem?.name || 'Item'} is out of stock — removed from cart`,
            'warning'
          );
          if (correctedCart.length > 0) await attempt(correctedCart);
          return;
        }

        // Write-path guards (e.g. an inactive product/customer) reply 422 with
        // { error: "Validation failed", details: ["… is inactive and cannot be used"] }.
        // Surface the specific detail rather than the generic top-level error.
        const detail = Array.isArray(err.data?.details) && err.data.details.length > 0
          ? err.data.details.join(', ')
          : null;
        showToast(detail || err.data?.error || 'Failed to sync order. Please try again.', 'error');
      }
    };

    // Reset the chain on a prior failure so one rejected link doesn't wedge
    // every future sync; then queue this run behind whatever is in flight.
    syncChainRef.current = syncChainRef.current.catch(() => {}).then(() => attempt(cartToSync));
    return syncChainRef.current;
  }, [
    buildDraftCreatePayload,
    cartRef,
    createOrder,
    currentOrderIdRef,
    dispatch,
    orderCartItemsRef,
    orderNotesRef,
    selectedCustomer,
    selectedSalesperson,
    setCart,
    showToast,
    updateOrder,
  ]);

  // If the first item(s) were added before a customer was attached, the initial
  // order sync fails when the org requires a customer (currentOrderId stays null).
  // Once a customer gets attached, retry the sync so the order — and its totals —
  // actually get created on the backend.
  useEffect(() => {
    if (selectedCustomer?.id && !currentOrderIdRef.current && cartRef.current.length > 0) {
      syncCartToBackend(cartRef.current);
    }
  }, [selectedCustomer?.id]); // eslint-disable-line

  const updateQty = useCallback((id, newQty) => {
    let capNotice = null;
    const newCart = cartRef.current.map((item) => {
      if (item.id !== id) return item;
      if (newQty === '') return { ...item, qty: '' };
      const value = parseInt(newQty);
      if (Number.isNaN(value)) return item;
      const cap = stockCap(item);
      const capped = Math.min(cap, Math.max(1, value));
      if (capped < value) capNotice = `Only ${cap} left for ${item.name} — quantity adjusted`;
      return { ...item, qty: capped };
    });
    setCart(newCart);
    if (capNotice) showToast(capNotice, 'warning');
    if (updateQtyTimer.current) clearTimeout(updateQtyTimer.current);
    updateQtyTimer.current = setTimeout(() => {
      const validCart = newCart.filter((item) => item.qty !== '' && parseInt(item.qty) > 0);
      if (validCart.length > 0) syncCartToBackend(validCart);
    }, 600);
  }, [cartRef, setCart, showToast, syncCartToBackend]);

  const adjustQty = useCallback((id, delta) => {
    const target = cartRef.current.find((item) => item.id === id);
    if (!target) return;
    const currentQty = parseInt(target.qty) || 0;
    const nextQty = Math.max(1, currentQty + delta);
    if (delta > 0 && nextQty > stockCap(target)) {
      showToast(`Only ${stockCap(target)} left for ${target.name}`, 'warning');
      return;
    }
    const newCart = cartRef.current.map((item) => item.id === id ? { ...item, qty: nextQty } : item);
    setCart(newCart);
    syncCartToBackend(newCart);
  }, [cartRef, setCart, showToast, syncCartToBackend]);

  const handleAddToCart = useCallback((product, isFromScan = false) => {
    if (billingMode === 'exchange' && exchangeSourceOrder) {
      if (!product.retail_price || parseFloat(product.retail_price) <= 0) {
        showToast(`${product.name}: No price set for this product at this store.`, 'error');
        return;
      }
      setExchangeNewItems((prev) => {
        const existing = prev.find((item) => item.product_id === product.id);
        if (existing) {
          return prev.map((item) => item.product_id === product.id ? { ...item, quantity: item.quantity + 1 } : item);
        }
        return [...prev, {
          product_id: product.id,
          product_name: product.name,
          sku: product.sku,
          display_barcode: product.display_barcode,
          retail_price: parseFloat(product.retail_price || 0),
          quantity: 1,
        }];
      });
      dispatch(setProductSearch(''));
      return;
    }

    if (!product.retail_price || parseFloat(product.retail_price) <= 0) {
      showToast(`${product.name}: No price set for this product at this store.`, 'error');
      return;
    }

    // Gate adding to cart (not just checkout) so the cashier attaches the
    // required customer/salesperson up front instead of building a cart the
    // org's checkout rules will later reject.
    const missingCustomer = customerRequired && !selectedCustomer;
    const missingSalesperson = salespersonRequired && !selectedSalesperson;
    if (missingCustomer || missingSalesperson) {
      const missing = [missingCustomer && 'customer', missingSalesperson && 'salesperson'].filter(Boolean).join(' and ');
      showToast(`Select ${missing} before adding products`, 'warning');
      if (missingCustomer) setIsCustomerModalOpen(true);
      else setIsSalespersonDrawerOpen(true);
      return;
    }

    // The backend precheck (see docs/features/stock-availability-precheck) is
    // the authoritative check and would reject this anyway — but blocking here
    // too avoids a doomed add sitting in the cart with a checkout total that
    // never reflects it.
    const stock = product.stock;
    if (stock && (stock.in_stock === false || stock.total_quantity === 0)) {
      showToast(`${product.name} is out of stock`, 'error');
      return;
    }

    const prevCart = cartRef.current;
    const existingItem = prevCart.find((item) => item.id === product.id);
    const nextQty = (existingItem ? Number(existingItem.qty) || 0 : 0) + 1;
    if (nextQty > stockCap(product)) {
      showToast(`Only ${stockCap(product)} left for ${product.name}`, 'warning');
      return;
    }

    if (stock && stock.total_quantity > 0 && stock.total_quantity <= 5) {
      showToast(`Low stock: only ${stock.total_quantity} left for ${product.name}`, 'warning');
    }

    const newCart = existingItem
      ? prevCart.map((item) => item.id === product.id ? { ...item, qty: nextQty } : item)
      : [...prevCart, { ...product, qty: 1 }];

    setCart(newCart);
    setSelectedProduct(product);
    dispatch(setProductSearch(''));
    syncCartToBackend(newCart);

    if (isFromScan) {
      setSnackbar({ open: true, message: `Added ${product.name}!`, severity: 'success' });
    }
  }, [
    billingMode,
    cartRef,
    customerRequired,
    dispatch,
    exchangeSourceOrder,
    salespersonRequired,
    selectedCustomer,
    selectedSalesperson,
    setCart,
    setExchangeNewItems,
    setIsCustomerModalOpen,
    setIsSalespersonDrawerOpen,
    setSelectedProduct,
    setSnackbar,
    showToast,
    syncCartToBackend,
  ]);

  const handleAddCarryBag = useCallback((bag, qty) => {
    const bagProduct = {
      id: bag.id,
      name: bag.name,
      sku: bag.sku,
      retail_price: bag.retail_price,
      stock: { total_quantity: bag.stock_at_store, in_stock: bag.stock_at_store > 0 },
    };
    const prevCart = cartRef.current;
    const existingItem = prevCart.find((item) => item.id === bagProduct.id);
    const newCart = existingItem
      ? prevCart.map((item) => item.id === bagProduct.id ? { ...item, qty: (Number(item.qty) || 0) + qty } : item)
      : [...prevCart, { ...bagProduct, qty }];
    setCart(newCart);
    setSelectedProduct(bagProduct);
    syncCartToBackend(newCart);
  }, [cartRef, setCart, setSelectedProduct, syncCartToBackend]);

  const handleNotesBlur = useCallback(async () => {
    const orderId = currentOrderIdRef.current;
    if (!orderId) return;
    try {
      await updateOrder({ id: orderId, notes: orderNotesRef.current }).unwrap();
    } catch (_) {}
  }, [currentOrderIdRef, orderNotesRef, updateOrder]);

  const handleRemoveCustomer = useCallback(async () => {
    const orderId = currentOrderIdRef.current;
    if (!orderId) {
      dispatch(clearCustomer());
      return;
    }

    try {
      const res = await updateOrder({ id: orderId, customer_id: null }).unwrap();
      let updatedOrder = res?.data?.order || res?.data;
      // Any held loyalty redemption belonged to the removed customer — clear it.
      if (updatedOrder && parseFloat(updatedOrder.loyalty_redemption_amount || 0) > 0) {
        try {
          const loyaltyRes = await removeLoyalty(orderId).unwrap();
          updatedOrder = loyaltyRes?.data?.order || updatedOrder;
        } catch (_) {}
      }
      if (updatedOrder) {
        dispatch(setCurrentOrder(updatedOrder));
      } else {
        dispatch(clearCustomer());
      }
      showToast('Customer removed', 'success');
    } catch (err) {
      showToast(err?.data?.error || 'Failed to remove customer', 'error');
    }
  }, [currentOrderIdRef, dispatch, showToast, updateOrder, removeLoyalty]);

  const handleRemoveOrderSalesperson = useCallback(async () => {
    const orderId = currentOrderIdRef.current;
    if (!orderId) {
      dispatch(setOrderSalesperson(null));
      return;
    }

    try {
      const res = await updateOrder({ id: orderId, salesperson_id: null }).unwrap();
      const updatedOrder = res?.data?.order || res?.data;
      if (updatedOrder) {
        dispatch(setCurrentOrder(updatedOrder));
      } else {
        dispatch(setOrderSalesperson(null));
      }
      showToast('Salesperson removed', 'success');
    } catch (err) {
      showToast(err?.data?.error || 'Failed to remove salesperson', 'error');
    }
  }, [currentOrderIdRef, dispatch, showToast, updateOrder]);

  const handleRemoveItemSalesperson = useCallback(async (orderItemId) => {
    dispatch(setItemSalesperson({ order_item_id: orderItemId, salesperson_id: null, salesperson_name: null }));

    const orderId = currentOrderIdRef.current;
    if (!orderId) return;

    try {
      const res = await updateOrder({
        id: orderId,
        order_items_attributes: [{ id: orderItemId, salesperson_id: null }],
      }).unwrap();
      dispatch(setCurrentOrder(res.data.order));
      showToast('Salesperson removed from item', 'success');
    } catch (err) {
      showToast(err?.data?.error || 'Failed to remove salesperson', 'error');
    }
  }, [currentOrderIdRef, dispatch, showToast, updateOrder]);

  const removeFromCart = useCallback(async (id) => {
    const newCart = cartRef.current.filter((item) => item.id !== id);
    setCart(newCart);
    if (selectedProduct?.id === id) setSelectedProduct(null);

    if (newCart.length === 0) {
      const orderId = currentOrderIdRef.current;
      if (orderId) {
        try {
          await cancelOrder(orderId).unwrap();
        } catch (_) {}
      }
      dispatch(clearOrder());
    } else {
      syncCartToBackend(newCart);
    }
  }, [cancelOrder, cartRef, currentOrderIdRef, dispatch, selectedProduct, setCart, setSelectedProduct, syncCartToBackend]);

  // `opts` is optional: button/F4 callers pass an event or nothing (drawer opens as
  // before); Gift Voucher mode passes { openDrawer: false } to switch straight over.
  // Resolves true when the bill was held.
  const handleHoldBill = useCallback(async (opts) => {
    const openDrawer = opts?.openDrawer !== false;
    const orderId = currentOrderIdRef.current;
    if (!orderId) {
      showToast('Add items to hold a bill', 'warning');
      return false;
    }
    try {
      await holdOrder(orderId).unwrap();
      dispatch(clearOrder());
      setCart([]);
      setSelectedProduct(null);
      setOrderNotes('');
      showToast('Order placed on hold!', 'success');
      if (openDrawer) setIsHoldDrawerOpen(true);
      return true;
    } catch (err) {
      showToast(err.data?.error || 'Failed to hold order', 'error');
      return false;
    }
  }, [currentOrderIdRef, dispatch, holdOrder, setCart, setIsHoldDrawerOpen, setOrderNotes, setSelectedProduct, showToast]);

  const handleResume = useCallback(async (order) => {
    const orderId = currentOrderIdRef.current;
    if (orderId && orderId !== order.id) {
      cancelOrder(orderId).catch(() => {});
    }
    let fullOrder = order;
    try {
      const res = await triggerGetOrder(order.id).unwrap();
      fullOrder = res.data?.order || res.data || order;
    } catch (_) {}

    const items = fullOrder.order_items || fullOrder.items || [];
    // Fetch each line's product fresh rather than reading a bulk local
    // cache — this is the one spot that used to depend on posCache holding
    // the full catalog; a handful of small per-item requests on resume is
    // fine, and it means the stock badge shown for a resumed line always
    // reflects what's actually on hand right now, not whatever it was at
    // the last catalog sync.
    const resumedCart = await Promise.all(items.map(async (item) => {
      let productDetails = {};
      try {
        const res = await triggerGetProductById(item.product_id).unwrap();
        productDetails = res?.data || {};
      } catch (_) {}
      return {
        ...productDetails,
        id: item.product_id,
        name: item.product_name,
        sku: item.product_sku,
        retail_price: item.unit_price,
        qty: parseFloat(item.quantity),
      };
    }));
    dispatch(setCurrentOrder(fullOrder));
    setCart(resumedCart);
    setSelectedProduct(null);
    setOrderNotes(fullOrder.notes || '');
    setIsHoldDrawerOpen(false);
  }, [cancelOrder, currentOrderIdRef, dispatch, setCart, setIsHoldDrawerOpen, setOrderNotes, setSelectedProduct, triggerGetOrder, triggerGetProductById]);

  const resetOrderState = useCallback(() => {
    setCart([]);
    setSelectedProduct(null);
    setOrderNotes('');
    dispatch(clearOrder());
  }, [dispatch, setCart, setOrderNotes, setSelectedProduct]);

  const handleNewSale = useCallback(() => {
    setIsCheckoutOpen(false);
    resetOrderState();
  }, [resetOrderState, setIsCheckoutOpen]);

  const handlePaymentCompleted = useCallback(() => {
    resetOrderState();
  }, [resetOrderState]);

  const handleDiscard = useCallback(async () => {
    const orderId = currentOrderIdRef.current;
    if (orderId) {
      try {
        await cancelOrder(orderId).unwrap();
      } catch (_) {}
    }
    setCart([]);
    setSelectedProduct(null);
    setOrderNotes('');
    dispatch(clearOrder());
  }, [cancelOrder, currentOrderIdRef, dispatch, setCart, setOrderNotes, setSelectedProduct]);

  const displayTotal = useMemo(() => {
    if (grand_total && parseFloat(grand_total) > 0) return parseFloat(grand_total);
    return cart.reduce((acc, item) => acc + (parseFloat(item.retail_price) || 0) * (parseInt(item.qty) || 0), 0);
  }, [grand_total, cart]);

  return {
    adjustQty,
    displayTotal,
    handleAddCarryBag,
    handleAddToCart,
    handleDiscard,
    handleHoldBill,
    handleNewSale,
    handleNotesBlur,
    handlePaymentCompleted,
    handleRemoveCustomer,
    handleRemoveOrderSalesperson,
    handleRemoveItemSalesperson,
    handleResume,
    removeFromCart,
    updateQty,
  };
}
